import useNotificationPreferencesStore from "@/store/notifications/notificationPreferencesStore";
import useNotificationStore from "@/store/notificationStore";
import tradeDataStore from "@/store/tradeDataStore";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import {
  getSoundForStatus,
  getSoundFilePath,
  getPreferenceKey,
  NotificationNegotiationEvent,
  SOUND_OPTIONS,
} from "@/lib/notificationConfig";

/**
 * Maps sound settings from the store to their corresponding file names
 * @deprecated Use SOUND_OPTIONS from notificationConfig.ts instead
 */
const soundOptions: Record<string, { label: string; file: string }> = {};
SOUND_OPTIONS.forEach((opt) => {
  soundOptions[opt.value] = { label: opt.label, file: opt.file };
});

/**
 * Plays a sound notification based on the sound setting
 * @param soundSetting - The sound setting value (e.g., "notification1")
 */
export function playNotificationSound(soundSetting: string): void {
  if (!soundSetting || soundSetting === "none") return;

  const filePath = getSoundFilePath(soundSetting);
  if (!filePath) return;

  try {
    const audio = new Audio(`/sounds/${filePath}`);
    audio.play().catch((error) => {
      console.error("Error playing notification sound:", error);
    });
  } catch (error) {
    console.error("Error creating audio element:", error);
  }
}

/**
 * Gets the appropriate sound setting and plays notification for a trade status change.
 * Uses the notification preferences store to determine if sound/bell should fire.
 * @param newStatus - The new status of the trade
 * @param tradeId - The trade ID
 * @returns The message to display (if any)
 */
export function notifyTradeStatusChange(
  newStatus: ExecutionStrategyStatus,
  tradeId?: string,
): string | null {
  const prefStore = useNotificationPreferencesStore.getState();
  const statusPrefs = prefStore.statusPreferences[newStatus];

  // If no preferences exist for this status, no notification
  if (!statusPrefs) return null;

  const shouldPlaySound =
    prefStore.globalSoundEnabled && statusPrefs.sound;
  const shouldAddBell =
    prefStore.globalBellEnabled && statusPrefs.bell;

  // If neither sound nor bell is enabled, skip entirely
  if (!shouldPlaySound && !shouldAddBell) return null;

  // Play sound if enabled
  if (shouldPlaySound) {
    const soundFile = getSoundForStatus(newStatus, prefStore.groupSounds);
    playNotificationSound(soundFile);
  }

  // Build message
  const statusLabel = newStatus;
  const message = tradeId
    ? `Trade ${tradeId} status: ${statusLabel}`
    : `Trade status: ${statusLabel}`;

  // Only return message if bell is enabled (message drives bell notification)
  return shouldAddBell ? message : null;
}

/**
 * Interface for notification options
 */
export interface NotificationOptions {
  /** The new status of the trade */
  newStatus: ExecutionStrategyStatus;
  /** The old status of the trade (optional) */
  oldStatus?: ExecutionStrategyStatus;
  /** The trade ID or ESID */
  tradeId?: string;
  /** The trade role — "OP" or "RP" */
  role?: string;
  /** The trade owner — e.g. "Mitch O'Brien (ARD)" */
  owner?: string;
  /** The latest activity on the trade — e.g. "Unawarded", "Quoted" */
  latestActivity?: string;
  /** Whether to show a console log message */
  showConsoleLog?: boolean;
}

/**
 * Main notification function that handles sounds and messages
 * @param options - Notification options
 */
export function handleTradeNotification(options: NotificationOptions): void {
  const { newStatus, oldStatus, tradeId, role, owner, latestActivity, showConsoleLog = true } = options;

  // Only notify if status actually changed
  if (oldStatus && oldStatus === newStatus) {
    return;
  }

  // Resolve role-aware preference key for Quoted/ReSent
  const prefKey = getPreferenceKey(newStatus, role);

  // Check preferences and play sound / build message
  const prefStore = useNotificationPreferencesStore.getState();
  const statusPrefs = prefStore.statusPreferences[prefKey];

  if (!statusPrefs) return;

  const shouldPlaySound =
    prefStore.globalSoundEnabled && statusPrefs.sound;
  const shouldAddBell =
    prefStore.globalBellEnabled && statusPrefs.bell;
  const shouldShowBrowser =
    prefStore.globalBrowserEnabled && statusPrefs.browser;

  if (!shouldPlaySound && !shouldAddBell && !shouldShowBrowser) return;

  // Play sound using the preference key so it resolves to the correct group
  if (shouldPlaySound) {
    const soundFile = getSoundForStatus(prefKey, prefStore.groupSounds);
    playNotificationSound(soundFile);
  }

  // Build message for bell and browser — append latestActivity when it differs from status
  const statusLabel = newStatus;
  const activitySuffix =
    latestActivity && latestActivity !== newStatus
      ? ` (${latestActivity})`
      : "";
  const message = tradeId
    ? `Trade ${tradeId} status: ${statusLabel}${activitySuffix}`
    : `Trade status: ${statusLabel}${activitySuffix}`;

  // Log to console if requested
  if (showConsoleLog) {
    console.log(`[Trade Notification] ${message}`);
  }

  // Add to notification store (bell icon) if bell is enabled
  if (shouldAddBell && tradeId) {
    const notificationStore = useNotificationStore.getState();
    const notificationType = getNotificationType(newStatus);
    notificationStore.addNotification(message, notificationType, tradeId, role, owner, latestActivity);
  }

  // Show browser notification if enabled (silent: true suppresses Chrome system sound)
  if (shouldShowBrowser && typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      const notification = new Notification("Trade Update", { body: message, silent: true });
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (tradeId) {
          tradeDataStore.getState().updateEisdsToFlash([tradeId]);
        }
      }
    } catch (error) {
      console.error("Error showing browser notification:", error);
    }
  }
}

/**
 * Maps ExecutionStrategyStatus to a notification type string for the bell center.
 * Returns the enum value as-is so it matches NOTIFICATION_GROUPS for colour lookup.
 */
export function getNotificationType(status: ExecutionStrategyStatus): string {
  return status;
}

/**
 * LQC-637 / LQC-1745 — fire a notification for an RP/OP Request Information
 * negotiation event (Request Received / Accepted / Rejected). Mirrors
 * handleTradeNotification: respects the master + per-event sound/bell/browser
 * toggles, plays the negotiation group's sound, adds a bell entry, and shows a
 * browser popup. The esid is passed through as the notification's tradeId so a
 * click flashes the relevant trade row via updateEisdsToFlash — the same path
 * status notifications use.
 */
export function notifyRequestInformationEvent(options: {
  event: NotificationNegotiationEvent;
  esid: string;
  role?: string;
  owner?: string;
  showConsoleLog?: boolean;
}): void {
  const { event, esid, role, owner, showConsoleLog = true } = options;

  const prefStore = useNotificationPreferencesStore.getState();
  const eventPrefs = prefStore.statusPreferences[event];
  if (!eventPrefs) return;

  const shouldPlaySound = prefStore.globalSoundEnabled && eventPrefs.sound;
  const shouldAddBell = prefStore.globalBellEnabled && eventPrefs.bell;
  const shouldShowBrowser =
    prefStore.globalBrowserEnabled && eventPrefs.browser;

  if (!shouldPlaySound && !shouldAddBell && !shouldShowBrowser) return;

  // Play sound using the event key so it resolves to the negotiation group sound.
  if (shouldPlaySound) {
    const soundFile = getSoundForStatus(event, prefStore.groupSounds);
    playNotificationSound(soundFile);
  }

  const message = `Trade ${esid}: ${event}`;

  if (showConsoleLog) {
    console.log(`[Negotiation Notification] ${message}`);
  }

  // Bell entry — esid as tradeId drives click-to-flash; event as type drives the
  // group colour via getStatusColor's group fallback.
  if (shouldAddBell) {
    useNotificationStore
      .getState()
      .addNotification(message, event, esid, role, owner, event);
  }

  // Browser popup — onclick focuses + flashes the trade row, same as the status path.
  if (
    shouldShowBrowser &&
    typeof Notification !== "undefined" &&
    Notification.permission === "granted"
  ) {
    try {
      const notification = new Notification("Trade Update", {
        body: message,
        silent: true,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
        tradeDataStore.getState().updateEisdsToFlash([esid]);
      };
    } catch (error) {
      console.error("Error showing negotiation browser notification:", error);
    }
  }
}
